import { VideoSpatialLayer } from "gather-video-common/dist/src/public/types";
import { isPlainObject, debounce } from "lodash";
import { difference } from "ramda";
import { getSpaceIdFromPath } from "gather-common/dist/src/public/utils";
import { GatedFeatName } from "gather-client-common/dist/src/public/gatekeeping/FEATURES";
import { gatherVideoClientDataSource } from "../../Globals";
const LOW_BREAKPOINT_ACTIVE_VIDEOS = 3;
const MID_BREAKPOINT_ACTIVE_VIDEOS = 8;
const LOW_BREAKPOINT_WIDTH = 1280 / 3;
const LOW_BREAKPOINT_HEIGHT = 720 / 3;
const MID_BREAKPOINT_WIDTH = 1280 / 1.5;
const MID_BREAKPOINT_HEIGHT = 720 / 1.5;
const LOWER_QUALITY_LOW_BREAKPOINT_WIDTH = LOW_BREAKPOINT_WIDTH + 1280 / 72;
const LOWER_QUALITY_LOW_BREAKPOINT_HEIGHT = LOW_BREAKPOINT_HEIGHT + 720 / 72;
const LOWER_QUALITY_MID_BREAKPOINT_WIDTH = MID_BREAKPOINT_WIDTH + 1280 / 8;
const LOWER_QUALITY_MID_BREAKPOINT_HEIGHT = MID_BREAKPOINT_HEIGHT + 720 / 8;
const PRODUCED_VIDEO_ASPECT_RATIO = 16 / 9;
export const isAdaptiveStreamSettings = (options) => "priorityStreams" in options && isPlainObject(options);
export class RemoteBandwidthManager {
    _adaptiveStreams = false;
    info = {};
    dimensionMap = {};
    _priorityStreams = [];
    get priorityStreams() {
        return this._priorityStreams;
    }
    set priorityStreams(streams) {
        this._priorityStreams = streams;
        this._updateStreamQuality();
        this.setHighestPriorityStreams(streams);
    }
    _isPriority = isPriority;
    onInfo = (info) => {
        difference(Object.keys(this.dimensionMap), Object.keys(info)).forEach((id) => {
            delete this.dimensionMap[id];
        });
        this.info = { ...info };
        this._updateStreamQuality();
    };
    setMaxConsumerLayers = async (_id, _tag, _spatialLayer, _temporalLayer) => { };
    setHighestPriorityStreams = (_id) => { };
    setAdaptiveStreamSettings = (options) => {
        const { priorityStreams } = options;
        if (priorityStreams) {
            this.priorityStreams = priorityStreams;
        }
    };
    set adaptiveStreams(active) {
        this._adaptiveStreams = active;
        this._updateStreamQuality();
    }
    get adaptiveStreams() {
        return this.adaptiveStreams;
    }
    setAdaptiveStreamDesiredResolution(id, tag, width, height) {
        if (tag !== "video")
            return;
        const dimension = { width, height };
        this.dimensionMap[id] = dimension;
        if (this._adaptiveStreams && this.info[id]?.video) {
            const numberOfActiveVideos = getActiveVideoElements(this.info);
            this.setMaxConsumerLayers(id, "video", getSpatialLayer(dimension, numberOfActiveVideos, this._isPriority(id)));
        }
    }
    _updateStreamQuality = debounce(() => {
        const numberOfActiveVideos = getActiveVideoElements(this.info);
        Object.entries(this.info).forEach(([id, info]) => {
            const video = info.video;
            if (video) {
                const dimension = this.dimensionMap[id];
                if (dimension) {
                    const isPriority = this._isPriority(id);
                    const layer = this._adaptiveStreams || isPriority
                        ? getSpatialLayer(dimension, numberOfActiveVideos, isPriority)
                        : VideoSpatialLayer.LowQuality;
                    this.setMaxConsumerLayers(id, "video", layer);
                }
                else {
                    this.setMaxConsumerLayers(id, "video", undefined);
                }
            }
        });
    }, 100);
}
export function isPriority(id) {
    return this.priorityStreams.includes(id);
}
export const getSpatialLayer = (dimension, activeVideos, isPriority) => Math.min(calculateSpatialLayerByDimension(dimension), isPriority ? VideoSpatialLayer.HighQuality : getSpatialLayerByActiveVideos(activeVideos));
export const getProducedVideoDimensionsFromVideoElementDimensions = ({ width, height, }) => {
    const doesHeightMatchProducedHeight = width <= height * PRODUCED_VIDEO_ASPECT_RATIO;
    const heightAtProducedAspectRatio = doesHeightMatchProducedHeight
        ? height
        : width * (1 / PRODUCED_VIDEO_ASPECT_RATIO);
    const widthAtProducedAspectRatio = doesHeightMatchProducedHeight
        ? height * PRODUCED_VIDEO_ASPECT_RATIO
        : width;
    return { width: widthAtProducedAspectRatio, height: heightAtProducedAspectRatio };
};
export const calculateSpatialLayerByDimension = (dimension) => {
    const spaceId = getSpaceIdFromPath(window.location.pathname);
    const hasLowerQualityVideos = gatherVideoClientDataSource.gate(GatedFeatName.NewMeetingUX, null, spaceId);
    const { width, height } = getProducedVideoDimensionsFromVideoElementDimensions(dimension);
    let recommended = VideoSpatialLayer.HighQuality;
    if (hasLowerQualityVideos) {
        if (LOWER_QUALITY_LOW_BREAKPOINT_HEIGHT > height ||
            LOWER_QUALITY_LOW_BREAKPOINT_WIDTH > width) {
            recommended = VideoSpatialLayer.LowQuality;
        }
        else if (LOWER_QUALITY_MID_BREAKPOINT_HEIGHT > height ||
            LOWER_QUALITY_MID_BREAKPOINT_WIDTH > width) {
            recommended = VideoSpatialLayer.MidQuality;
        }
        return recommended;
    }
    if (LOW_BREAKPOINT_HEIGHT > height || LOW_BREAKPOINT_WIDTH > width) {
        recommended = VideoSpatialLayer.LowQuality;
    }
    else if (MID_BREAKPOINT_HEIGHT > height || MID_BREAKPOINT_WIDTH > width) {
        recommended = VideoSpatialLayer.MidQuality;
    }
    return recommended;
};
export const getSpatialLayerByActiveVideos = (activeVideos) => {
    let recommended = VideoSpatialLayer.HighQuality;
    if (activeVideos > MID_BREAKPOINT_ACTIVE_VIDEOS) {
        recommended = VideoSpatialLayer.LowQuality;
    }
    else if (activeVideos > LOW_BREAKPOINT_ACTIVE_VIDEOS) {
        recommended = VideoSpatialLayer.MidQuality;
    }
    return recommended;
};
export const getActiveVideoElements = (info) => Object.values(info).reduce((current, next) => {
    const numVideoActive = hasActive(next, "video") ? 1 : 0;
    const numScreenActive = hasActive(next, "screen") ? 1 : 0;
    return current + numVideoActive + numScreenActive;
}, 0);
export const hasActive = (consumerInfo, kind) => consumerInfo?.enabled[kind] && !consumerInfo?.muted[kind];
//# sourceMappingURL=RemoteBandwidthManager.js.map